import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Flame, RefreshCw } from 'lucide-react'
import { Badge, Button, PageContainer, PageHeader, StatePanel } from '@/components/common'
import { useOrganization } from '@/contexts/OrganizationContext'
import { prepacksApi } from '@/services/api'
import { PrepackBoostCard } from './PrepackBoostCard'
import { normalizeBoost, syncedLabel } from './prepackBoostModel'
import styles from './PrepacksBoostsPage.module.css'

// Boost sync is an always-on job: while this tab is open it polls the feed and
// every boost is auto-added to the Library and pinned to the top of its surfaces.
const BOOST_POLL_MS = 30_000

export default function PrepacksBoostsPage() {
  const { currentOrg } = useOrganization()
  const slug = currentOrg?.slug
  const boostsQuery = useQuery({
    queryKey: ['prepacks', 'boosts', slug],
    queryFn: () => prepacksApi.listBoosts(slug),
    enabled: Boolean(slug),
    refetchInterval: (query) => (query.state.data?.connected || query.state.data?.sample ? BOOST_POLL_MS : false),
  })
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(id)
  }, [])

  const data = boostsQuery.data
  const boosts = useMemo(() => (data?.items || []).map(normalizeBoost), [data])
  const active = data?.connected || data?.sample
  const syncing = boostsQuery.isFetching

  const status = !data ? (boostsQuery.isError ? 'Not connected' : 'Loading…')
    : !active ? 'Not connected'
      : syncing ? 'Syncing…'
        : `${boosts.length} live · synced ${syncedLabel(data.synced_at, now) || 'just now'}`
  const empty = boostsQuery.isError || (data && (!active || boosts.length === 0))

  return (
    <PageContainer>
      <PageHeader
        title={<span className={styles.title}><Flame className={styles.titleIcon} aria-hidden="true" />Boosts</span>}
        badge={data?.sample ? <Badge tone="warning" compact>Sample data</Badge> : null}
        subtitle={(
          <span className={styles.status} data-state={!data ? 'loading' : active ? (syncing ? 'syncing' : 'live') : 'off'}>
            <span className={styles.statusDot} aria-hidden="true" />
            {status} · auto-added to Library, pinned to top of all surfaces
          </span>
        )}
        actions={(
          <Button variant="secondary" size="compact" onClick={() => boostsQuery.refetch()} disabled={syncing || !slug}>
            <RefreshCw className={`h-4 w-4 ${syncing ? styles.spin : ''}`} aria-hidden="true" />
            Refresh
          </Button>
        )}
      />

      {boostsQuery.isLoading ? (
        <div className={styles.grid} aria-busy="true" aria-label="Loading boosts">
          {[0, 1, 2].map((i) => <div key={i} className={styles.skeleton} />)}
        </div>
      ) : empty ? (
        <StatePanel
          state="empty"
          title="There are no boosts currently"
          description="Live sportsbook boosts will appear here, be added to the Library automatically, and pin to the top of every surface."
        />
      ) : (
        <div className={styles.grid} aria-label="Live boosts">
          {boosts.map((boost) => <PrepackBoostCard key={boost.id} boost={boost} now={now} />)}
        </div>
      )}
    </PageContainer>
  )
}
